type CapitalizeFirst<T extends string> = T extends `${infer First}${infer Rest}`
  ? `${Uppercase<First>}${Rest}`
  : T

export const capitalizeFirstLetter = <T extends string>(
  str: T
): CapitalizeFirst<T> =>
  ((str || "").charAt(0).toUpperCase() + str.slice(1)) as CapitalizeFirst<T>

export const capitalize = (str: string) => {
  try {
    return str.replace(/(^|[^a-zA-Z])([a-z])/g, (match) => match.toUpperCase())
  } catch {
    return str
  }
}

export const replaceLinkInString = (str: string) =>
  str.replace(
    /(\b(https):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim,
    '<a href="$1" target="_blank">$1</a>'
  )

export const sanitizeString = (
  str: string,
  options: { allowCode?: boolean; allowTags?: string[] } = {}
) => {
  const SYMBOLS = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;"
  } as Record<string, string>

  const reg = /&(?!(amp|lt|gt|quot|#x27|#x2F);)|[<>"'/]/gi
  const { allowTags, allowCode } = options

  const sanitize = (text: string) => {
    if (!allowTags?.length) {
      return text.replace(reg, (match) => SYMBOLS[match]) // Changed from str to part
    }

    const escapeText = (s: string) => {
      return s.replace(/[&<>"'/]/g, (match) => SYMBOLS[match])
    }

    const tagReg = /(<[^>]+>)([^<]*)/g

    return text.replace(tagReg, (_, tag, content) => {
      // Changed from str to part
      const tagName = tag.match(/^<\/?([a-zA-Z][a-zA-Z0-9]*)/)

      if (tagName && allowTags.includes(tagName[1].toLowerCase())) {
        return tag + escapeText(content)
      }

      return escapeText(tag) + escapeText(content)
    })
  }

  if (allowCode) {
    const parts = str.split(/(<code>.*?<\/code>)/gi)

    return parts
      .map((part) => {
        if (part.match(/^<code>.*<\/code>$/gi)) {
          return part.replace(/(<script\b[^>]*>.*?<\/script>)/gi, (match) =>
            match.replace(/[&<>"'/]/g, (char) => SYMBOLS[char])
          )
        } else {
          return sanitize(part)
        }
      })
      .join("")
  }

  return sanitize(str)
}

export const sliceConcat = (str: string | undefined, length: number) => {
  if (!str) {
    return ""
  }
  if (str.length > length) {
    return str.slice(0, length) + "..."
  }
  return str
}

export const trimString = <V>(value: V) =>
  typeof value === "string" ? value.trim() : value

export const trim = <V>(value: V) => {
  if (Array.isArray(value)) return value.map(trimString)

  return trimString(value)
}

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export function toLowerCase(str: string): string
export function toLowerCase<T>(str: T): T
export function toLowerCase<T>(str: T): T | string {
  return typeof str === "string" ? str.toLowerCase() : str
}

export const getStringForCompare = (str: string, symbol: string = ",") =>
  str.split(symbol).sort().join(symbol)

// An image as name:tag, or the name alone when there is no tag (a serverless pseudo image
// has none)
export const getImageNameWithTag = (name: string, tag?: string) =>
  tag ? `${name}:${tag}` : name
